// =======================================
// SHARED PASSWORD VALIDATION
// =======================================
// Single source of truth for the client-side password policy. Must stay in
// sync with the server policy in supabase/config.toml (minimum_password_length,
// password_requirements) and the hosted dashboard settings.

export const PASSWORD_REQUIREMENTS = {
  MIN_LENGTH: 10,
  REQUIRE_UPPERCASE: true,
  REQUIRE_LOWERCASE: true,
  REQUIRE_NUMBER: true
} as const;

export const PASSWORD_HELP_TEXT = `Password must be at least ${PASSWORD_REQUIREMENTS.MIN_LENGTH} characters and contain uppercase, lowercase, and numbers`;

export const PASSWORD_VALIDATION_MESSAGES = {
  REQUIRED: 'Password is required',
  MIN_LENGTH: `Password must be at least ${PASSWORD_REQUIREMENTS.MIN_LENGTH} characters`,
  WEAK: 'Password must contain at least one uppercase letter, one lowercase letter, and one number',
  CONFIRM_REQUIRED: 'Please confirm your password',
  NO_MATCH: 'Passwords do not match'
} as const;

/**
 * Validate a new password (and its confirmation) against the password policy.
 * Returns a user-facing error message, or null when valid.
 */
export function validateNewPassword(password: string, confirmPassword: string): string | null {
  if (!password) {
    return PASSWORD_VALIDATION_MESSAGES.REQUIRED;
  }
  if (password.length < PASSWORD_REQUIREMENTS.MIN_LENGTH) {
    return PASSWORD_VALIDATION_MESSAGES.MIN_LENGTH;
  }
  if (PASSWORD_REQUIREMENTS.REQUIRE_UPPERCASE && !/[A-Z]/.test(password)) {
    return PASSWORD_VALIDATION_MESSAGES.WEAK;
  }
  if (PASSWORD_REQUIREMENTS.REQUIRE_LOWERCASE && !/[a-z]/.test(password)) {
    return PASSWORD_VALIDATION_MESSAGES.WEAK;
  }
  if (PASSWORD_REQUIREMENTS.REQUIRE_NUMBER && !/\d/.test(password)) {
    return PASSWORD_VALIDATION_MESSAGES.WEAK;
  }
  if (!confirmPassword) {
    return PASSWORD_VALIDATION_MESSAGES.CONFIRM_REQUIRED;
  }
  if (password !== confirmPassword) {
    return PASSWORD_VALIDATION_MESSAGES.NO_MATCH;
  }
  return null;
}
